import axios from '@/plugins/axios'
import { Module } from 'vuex'

// Member Settings > Executive Profile: an executive's Core, audiences and publishing rhythm.
// The API cleans and shapes what's sent (ExecutiveProfile::normalize), so a save returns the
// profile as stored.

export interface ExecutiveBelief {
    text: string
    signature: boolean
}

export interface ExecutivePillar {
    name: string
    perWeek: number | null
    topics: string[]
}

export interface ExecutiveAudience {
    id: string
    name: string
    description: string
    roles: string[]
    icpIds: number[]
    ownIcps: string[]
    share: number | null
    positioning: { shouldThink: string; not: string }
    goal: string
    voice: { formality: number | null; detail: number | null; words: string[]; neverSay: string[] }
    pillars: ExecutivePillar[]
    success: string[]
}

export interface ExecutiveSlot {
    day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
    label: string
    audienceId: string | null
    pillar: string
}

export interface ExecutiveProfileData {
    core: {
        territory: string
        beliefs: ExecutiveBelief[]
        signatureIdeas: string[]
        voice: string[]
        notLike: string[]
        writingRules: { do: string[]; dont: string[] }
        neverSay: string[]
        mix: {
            postsPerWeek: number | null
            teach: number | null
            perspective: number | null
            promote: number | null
            productLed: number | null
        }
        notes: string
    }
    audiences: ExecutiveAudience[]
    rhythm: { slots: ExecutiveSlot[] }
}

export const newAudience = (name = ''): ExecutiveAudience => ({
    id: '',
    name,
    description: '',
    roles: [],
    icpIds: [],
    ownIcps: [],
    share: null,
    positioning: { shouldThink: '', not: '' },
    goal: '',
    voice: { formality: null, detail: null, words: [], neverSay: [] },
    pillars: [],
    success: [],
})

export interface ExecutiveDraft {
    status: 'queued' | 'running' | 'done' | 'failed' | null
    draft?: ExecutiveProfileData | null
    used?: {
        jobTitle: string | null
        seniority: string | null
        jobFunction: string | null
        posts: number
        icps: number
        insights: number
        keyTopics: number
        document: boolean
    } | null
    error?: string | null
    hasDocument?: boolean
}

const fresh = { cache: false }

const executiveProfileModule: Module<any, any> = {
    actions: {
        async getExecutiveProfile(_, memberId: number): Promise<ExecutiveProfileData> {
            const { data } = await axios.get(`/api/executive-profiles/${memberId}`, fresh)
            return data.profile
        },

        async updateExecutiveProfile(
            _,
            { memberId, profile }: { memberId: number; profile: ExecutiveProfileData },
        ): Promise<ExecutiveProfileData> {
            const { data } = await axios.put(`/api/executive-profiles/${memberId}`, { profile })
            return data.profile
        },

        // Draft with AI: queue a draft (optionally from a strategy document), follow it, dismiss it
        async requestExecutiveDraft(_, { memberId, sourceText }: { memberId: number; sourceText?: string }): Promise<ExecutiveDraft> {
            const { data } = await axios.post(`/api/executive-profiles/${memberId}/draft`, { sourceText: sourceText ?? '' })
            return data
        },

        async getExecutiveDraft(_, memberId: number): Promise<ExecutiveDraft> {
            const { data } = await axios.get(`/api/executive-profiles/${memberId}/draft`, { ...fresh, silent: true })
            return data
        },

        async dismissExecutiveDraft(_, memberId: number): Promise<ExecutiveDraft> {
            const { data } = await axios.delete(`/api/executive-profiles/${memberId}/draft`)
            return data
        },
    },
}

export default executiveProfileModule
